// @flow
/**
 * Onboarding Utilities
 *
 * Centralized logic for determining which onboarding step a user should be on
 * and where they should be redirected based on their account state.
 *
 * Onboarding Flow:
 * 1. /verifyPhone - User verifies their personal phone number
 * 2. /subscribe - User selects and pays for a subscription
 * 3. /pickYourPhoneNumber - User picks a business phone number
 * 4. /member - Main dashboard (onboarding complete)
 */

import { hasNoSubscription } from './billingValidationUtils';
import { log } from './jsUtils';

export type OnboardingState = {
  user: ?Object,
  billing: ?Object,
  defaultBuilding: ?Object
};

export type OnboardingStep =
  | '/verifyPhone'
  | '/subscribe'
  | '/pickYourPhoneNumber'
  | '/member';

/**
 * List of onboarding routes (excluding final destination)
 */
export const ONBOARDING_ROUTES: Array<string> = [
  '/verifyPhone',
  '/subscribe',
  '/pickYourPhoneNumber'
];

/**
 * Determine which onboarding step the user should be on based on their state.
 * Returns null if user has completed all onboarding steps.
 *
 * @param {OnboardingState} state - The user's current state from Redux
 * @returns {?OnboardingStep} The required onboarding step, or null if complete
 */
export const getRequiredOnboardingStep = (state: OnboardingState): ?OnboardingStep => {
  const { user, billing, defaultBuilding } = state;

  // Step 1: Phone verification
  if (!user?.IsPhoneVerified) {
    return '/verifyPhone';
  }

  // Step 2: Subscription
  if (hasNoSubscription(billing)) {
    return '/subscribe';
  }

  // Step 3: Business phone number
  if (!defaultBuilding?.Phone) {
    return '/pickYourPhoneNumber';
  }

  // All steps complete
  return null;
};

/**
 * Get the next step after completing the current onboarding step.
 * Used after successfully completing an action (e.g., after payment success).
 *
 * @param {OnboardingStep} currentStep - The step that was just completed
 * @returns {OnboardingStep} The next step in the onboarding flow
 */
export const getNextOnboardingStep = (currentStep: OnboardingStep): OnboardingStep => {
  const stepOrder: { [OnboardingStep]: OnboardingStep } = {
    '/verifyPhone': '/subscribe',
    '/subscribe': '/pickYourPhoneNumber',
    '/pickYourPhoneNumber': '/member'
  };
  return stepOrder[currentStep] || '/member';
};

/**
 * Check if user should be redirected away from their current page.
 * This handles both:
 * 1. Entry validation - user trying to access a step they shouldn't be on
 * 2. Completion redirect - user on an onboarding page but has completed all steps
 *
 * @param {string} currentPath - The current route path
 * @param {OnboardingState} state - The user's current state from Redux
 * @returns {?string} The path to redirect to, or null if user should stay
 */
export const getOnboardingRedirect = (
  currentPath: string,
  state: OnboardingState
): ?string => {
  const requiredStep = getRequiredOnboardingStep(state);

  log('[getOnboardingRedirect] currentPath:', currentPath, 'requiredStep:', requiredStep);

  // User has completed all onboarding
  if (requiredStep === null) {
    // If on an onboarding page, redirect to member
    if (ONBOARDING_ROUTES.includes(currentPath)) {
      log('[getOnboardingRedirect] Onboarding complete, redirecting from', currentPath, 'to /member');
      return '/member';
    }
    return null; // Stay on current page
  }

  // User needs to be on a specific onboarding step
  if (currentPath !== requiredStep) {
    log('[getOnboardingRedirect] Wrong step, redirecting from', currentPath, 'to', requiredStep);
    return requiredStep;
  }

  // User is on the correct page
  return null;
};

/**
 * Check if a given path is an onboarding route.
 *
 * @param {string} path - The route path to check
 * @returns {boolean} True if the path is an onboarding route
 */
export const isOnboardingRoute = (path: string): boolean => {
  return ONBOARDING_ROUTES.includes(path);
};
